import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RotateCcw, Trash2 } from "lucide-react";

const SAMPLE_HEX_TEXT = "Build outstanding tools with absolute precision.";

export function HexTextConverter() {
  const [input, setInput] = useState(SAMPLE_HEX_TEXT);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"text2hex" | "hex2text">("text2hex");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    try {
      if (!val.trim()) { setOutput(""); return; }
      if (currentMode === "text2hex") {
        setOutput(val.split("").map(c => c.charCodeAt(0).toString(16).padStart(2, "0")).join(" "));
      } else {
        const hexes = val.replace(/[^0-9A-Fa-f]/g, "").match(/.{1,2}/g) || [];
        setOutput(hexes.map(h => String.fromCharCode(parseInt(h, 16))).join(""));
      }
    } catch (e: any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "text2hex" | "hex2text") => {
    setMode(newMode);
    if (newMode === "text2hex") {
      setInput(SAMPLE_HEX_TEXT);
      process(SAMPLE_HEX_TEXT, "text2hex");
    } else {
      const hexSample = "42 75 69 6c 64 20 6f 75 74 73 74 61 6e 64 69 6e 67 20 74 6f 6f 6c 73";
      setInput(hexSample);
      process(hexSample, "hex2text");
    }
  };

  return (
    <ToolPageLayout
      title="Hex to Text & Text to Hex Converter Online"
      description="Convert standard text strings into hex (hexadecimal) representation or decode hex strings back to plain text instantly in your browser."
      h1="Hex / Text Converter"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Toggle 'Text to Hex' or 'Hex to Text'.",
        "Paste or type your input string.",
        "Copy your converted hexadecimal or alphabetic text values."
      ]}
      faq={[
        { question: "What is hexadecimal?", answer: "Hexadecimal is a base-16 numbering system using digits 0-9 and letters A-F to represent bytes concisely." },
        { question: "Is this tool offline-safe?", answer: "Yes, it runs 100% in local Javascript sandbox RAM." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("text2hex")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "text2hex" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Text to Hex
            </button>
            <button 
              onClick={() => handleModeChange("hex2text")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "hex2text" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Hex to Text
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Text</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="Type input here..." className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Converted Result</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_BIN_TEXT = "Code is art.";

export function BinaryTextConverter() {
  const [input, setInput] = useState(SAMPLE_BIN_TEXT);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"text2bin" | "bin2text">("text2bin");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    try {
      if (!val.trim()) { setOutput(""); return; }
      if (currentMode === "text2bin") {
        setOutput(val.split("").map(c => c.charCodeAt(0).toString(2).padStart(8, "0")).join(" "));
      } else {
        const bins = val.replace(/[^01]/g, "").match(/.{1,8}/g) || [];
        setOutput(bins.map(b => String.fromCharCode(parseInt(b, 2))).join(""));
      }
    } catch (e: any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "text2bin" | "bin2text") => {
    setMode(newMode);
    if (newMode === "text2bin") {
      setInput(SAMPLE_BIN_TEXT);
      process(SAMPLE_BIN_TEXT, "text2bin");
    } else {
      const binSample = "01000011 01101111 01100100 01100101";
      setInput(binSample);
      process(binSample, "bin2text");
    }
  };

  return (
    <ToolPageLayout
      title="Binary to Text & Text to Binary Converter Online"
      description="Convert standard alphanumeric text strings to binary code sequence patterns or parse binary back to readable text values instantly."
      h1="Binary / Text Converter"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Toggle 'Text to Binary' or 'Binary to Text'.",
        "Type or paste your source text.",
        "Retrieve your converted binary bytes or alphabetic characters."
      ]}
      faq={[
        { question: "What is binary code?", answer: "Binary is a base-2 system that uses sequences of 1s and 0s to represent electronic signals and instructions." },
        { question: "Is this tool secure?", answer: "Yes, all parsing runs entirely offline in-browser." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("text2bin")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "text2bin" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Text to Binary
            </button>
            <button 
              onClick={() => handleModeChange("bin2text")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "bin2text" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Binary to Text
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Text</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="Type input here..." className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Converted Result</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const MORSE_CODE_MAP: Record<string, string> = {
  "A": ".-", "B": "-...", "C": "-.-.", "D": "-..", "E": ".", "F": "..-.", "G": "--.", "H": "....", "I": "..", "J": ".---", "K": "-.-", "L": ".-..", "M": "--", "N": "-.", "O": "---", "P": ".--.", "Q": "--.-", "R": ".-.", "S": "...", "T": "-", "U": "..-", "V": "...-", "W": ".--", "X": "-..-", "Y": "-.--", "Z": "--..",
  "0": "-----", "1": ".----", "2": "..---", "3": "...--", "4": "....-", "5": ".....", "6": "-....", "7": "--...", "8": "---..", "9": "----.",
  ".": ".-.-.-", ",": "--..--", "?": "..--..", "'": ".----.", "!": "-.-.--", "/": "-..-.", "(": "-.--.", ")": "-.--.-", "&": ".-...", ":": "---...", ";": "-.-.-.", "=": "-...-", "+": ".-.-.", "-": "-....-", "_": "..--._", "\"": ".-..-.", "$": "...-..-", "@": ".--.-.", " ": "/"
};
const REVERSE_MORSE = Object.fromEntries(Object.entries(MORSE_CODE_MAP).map(([k, v]) => [v, k]));

const SAMPLE_MORSE_TEXT = "HELLO WORLD";

export function MorseCodeTranslator() {
  const [input, setInput] = useState(SAMPLE_MORSE_TEXT);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"text2morse" | "morse2text">("text2morse");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    try {
      if (!val.trim()) { setOutput(""); return; }
      if (currentMode === "text2morse") {
        setOutput(val.toUpperCase().split("").map(c => MORSE_CODE_MAP[c] || c).join(" "));
      } else {
        setOutput(val.split(" ").map(c => REVERSE_MORSE[c] || c).join(""));
      }
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "text2morse" | "morse2text") => {
    setMode(newMode);
    if (newMode === "text2morse") {
      setInput(SAMPLE_MORSE_TEXT);
      process(SAMPLE_MORSE_TEXT, "text2morse");
    } else {
      const morseSample = ".... . .-.. .-.. --- / .-- --- .-. .-.. -..";
      setInput(morseSample);
      process(morseSample, "morse2text");
    }
  };

  return (
    <ToolPageLayout
      title="Morse Code Translator & Converter Online"
      description="Translate standard alphabetic letters into Morse code dots and dashes, or convert Morse sequences back to clean text characters instantly."
      h1="Morse Code Translator"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Toggle 'Text to Morse' or 'Morse to Text'.",
        "Type or paste your characters (letters or dots/dashes).",
        "Copy your converted Morse code sequence or text sentence."
      ]}
      faq={[
        { question: "What is Morse code?", answer: "Morse code is a signaling system representing characters using customized sequences of short signals (dots) and long signals (dashes)." },
        { question: "Is my translation kept secure?", answer: "Yes, translating is handled 100% offline inside your browser RAM." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("text2morse")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "text2morse" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Text to Morse
            </button>
            <button 
              onClick={() => handleModeChange("morse2text")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "morse2text" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Morse to Text
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input text</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="Type input here..." className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Converted Result</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function EncodingTools2() {
  return (
    <ToolPageLayout
      title="Hex, Binary & Morse Code Converters Suite"
      description="Various conversion tools: Text to Hex, Text to Binary, and Morse code translation."
      h1="Encoding & Conversion Tools"
      category="Encoding Tools"
      categoryPath="/encoding-tools"
      howToUse={[
        "Select the converter (Hex, Binary, or Morse).",
        "Enter your input.",
        "Perform the conversion operation."
      ]}
      faq={[
        { question: "Are these tools free?", answer: "Yes, all our conversion tools are completely free to use." }
      ]}
    >
      <div className="space-y-12">
        <section><h2 className="text-xl font-bold mb-4">Hex Text Converter</h2><HexTextConverter /></section>
        <section><h2 className="text-xl font-bold mb-4">Binary Text Converter</h2><BinaryTextConverter /></section>
        <section><h2 className="text-xl font-bold mb-4">Morse Code Translator</h2><MorseCodeTranslator /></section>
      </div>
    </ToolPageLayout>
  );
}
